import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@/modern/utils";

const STEP_INTERVAL_MS = 4500;

const STEPS_QUESTIONS = [
  "Reading your sources",
  "Identifying gaps and ambiguities",
  "Drafting clarifying questions",
  "Almost ready",
];

const STEPS_BUILDING = [
  "Combining sources and answers",
  "Drafting sections",
  "Tightening for coherence",
  "Creating your Context",
];

interface AnalysingLoaderProps {
  variant: "questions" | "context";
}

export const AnalysingLoader = ({ variant }: AnalysingLoaderProps) => {
  const steps = variant === "questions" ? STEPS_QUESTIONS : STEPS_BUILDING;
  const title = variant === "questions" ? "Analysing your sources…" : "Building your Context…";
  const subtitle =
    variant === "questions"
      ? "Skimming the pages, flagging ambiguities, and picking the few clarifications that matter most. Please keep this open."
      : "Combining your sources and answers into a structured business context.";

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
    const intervalId = setInterval(() => {
      setActiveIndex((i) => Math.min(i + 1, steps.length - 1));
    }, STEP_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [variant, steps.length]);

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-12">
      {/* Pulsing icon */}
      <div className="relative mb-6">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
          <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center animate-pulse">
            <svg
              className="h-5 w-5 text-primary"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z"
              />
            </svg>
          </div>
        </div>
      </div>

      <h3 className="text-base font-semibold text-foreground mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground text-center max-w-sm mb-8">{subtitle}</p>

      {/* Steps checklist */}
      <div
        className="w-full max-w-xs space-y-2.5"
        aria-live="polite"
        aria-label="Progress"
      >
        {steps.map((step, i) => {
          const isCompleted = i < activeIndex;
          const isActive = i === activeIndex;
          const isPending = i > activeIndex;

          return (
            <div key={step} className="flex items-center gap-3">
              <div
                className={cn(
                  "h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-300",
                  isCompleted && "border-primary bg-primary",
                  isActive && "border-primary bg-primary/10",
                  isPending && "border-border"
                )}
              >
                {isCompleted && (
                  <Check className="h-2.5 w-2.5 text-primary-foreground" strokeWidth={3} />
                )}
                {isActive && (
                  <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                )}
              </div>
              <span
                className={cn(
                  "text-sm transition-colors duration-300",
                  (isCompleted || isActive) && "text-foreground",
                  isActive && "font-medium",
                  isPending && "text-muted-foreground"
                )}
              >
                {step}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
